import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BeatShell, ChipRow } from "@/components/nutrition/NutritionParts";
import { gradeChips, beatMax } from "../beatUtils";
import { Briefing } from "./Shared";
import { Gauge, Play, CheckCircle2 } from "lucide-react";

export function AutoclaveBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const target = beat.target || { temp: 121, pressure: 15, time: 15 };
  const [sel, setSel] = useState(new Set());
  const [progress, setProgress] = useState(0); // 0..1
  const [running, setRunning] = useState(false);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const raf = useRef(0);
  const done = progress >= 1;

  const toggle = (i) => { if (checked) return; setSel((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; }); };

  const runCycle = () => {
    if (running || done) return;
    setRunning(true);
    const start = performance.now();
    const dur = 2600;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setProgress(t);
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else setRunning(false);
    };
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const onPrimary = () => {
    if (!checked) {
      if (!done) return toast.error("Run the sterilisation cycle first.");
      const g = gradeChips(beat.chips, sel, beat.max ?? beat.full ?? 20);
      setRes({ earned: g.pts, max: beatMax({ ...beat, type: "multi" }), detail: `Caught ${g.caught}/${g.total}, ${g.falseTaps} false`, key: beat.key, label: beat.label });
    } else onDone(res);
  };

  const temp = Math.round(20 + progress * (target.temp - 20));
  const pressure = (progress * target.pressure).toFixed(1);
  const elapsed = Math.round(progress * target.time);

  return (
    <BeatShell
      Icon={Gauge}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Check practices"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      {/* autoclave gauge panel */}
      <div className="rounded-lg border-2 border-[#222] bg-[#0a0a0a] p-4" data-testid="autoclave-gauge">
        <div className="grid grid-cols-3 gap-3">
          {[
            { k: "Temp", v: `${temp}°C`, ok: done },
            { k: "Pressure", v: `${pressure} psi`, ok: done },
            { k: "Cycle", v: `${elapsed} min`, ok: done },
          ].map((m) => (
            <div key={m.k} className="text-center">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-clinical">{m.k}</div>
              <div className="text-xl font-mono-clinical font-bold" style={{ color: m.ok ? "#39FF14" : "#FFEA00" }}>{m.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 h-2 rounded-full bg-[#1e293b] overflow-hidden">
          <div className="h-full rounded-full transition-none" style={{ width: `${progress * 100}%`, background: done ? "#10b981" : "#f59e0b" }} />
        </div>

        {/* chemical indicator strip */}
        <div className="flex items-center gap-3 mt-3">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-clinical">Indicator strip</span>
          <div
            className="flex-1 h-6 rounded border border-slate-600 flex items-center justify-center text-[10px] font-bold tracking-widest transition-colors duration-500"
            data-testid="indicator-strip"
            style={{ background: done ? "repeating-linear-gradient(45deg,#1f2937,#1f2937 6px,#111827 6px,#111827 12px)" : "#d6c9a8", color: done ? "#fff" : "#7c6f4f" }}
          >
            {done ? "STERILISED ✓" : "NOT PROCESSED"}
          </div>
        </div>

        {!done && (
          <button
            type="button"
            onClick={runCycle}
            disabled={running}
            data-testid="run-cycle-btn"
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-[#0d9488] hover:bg-[#0f766e] rounded-md py-2 transition-colors disabled:opacity-50"
          >
            <Play className="w-4 h-4" /> {running ? "Running cycle…" : `Run cycle · ${target.temp}°C / ${target.time} min`}
          </button>
        )}
        {done && !checked && (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-400">
            <CheckCircle2 className="w-4 h-4" /> Cycle complete — now confirm best practice below
          </div>
        )}
      </div>

      <ChipRow chips={beat.chips} selected={sel} onToggle={toggle} revealed={checked} testidPrefix={`${beat.key}-chip`} />
      {checked && (
        <div className="mt-1 space-y-1.5" data-testid={`${beat.key}-coaching`}>
          {beat.chips.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
            </div>
          ))}
        </div>
      )}
    </BeatShell>
  );
}
